'use client';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';

interface Override {
  id: string;
  topic_keywords: string;
  override_text: string;
  source_url?: string;
}

interface Props {
  questionText: string;
}

let cachedOverrides: Override[] | null = null;
let cacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export default function AdminOverridesBanner({ questionText }: Props) {
  const [matched, setMatched] = useState<Override[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!questionText) return;

    const loadAndMatch = async () => {
      try {
        // Use cache to avoid hammering the API
        if (!cachedOverrides || Date.now() - cacheTime > CACHE_TTL) {
          const res = await fetch('/api/admin-override');
          if (res.ok) {
            const data = await res.json() as { overrides: Override[] };
            cachedOverrides = data.overrides || [];
            cacheTime = Date.now();
          }
        }

        if (!cachedOverrides?.length) return;

        const lower = questionText.toLowerCase();
        const matches = cachedOverrides.filter(ov => {
          const keywords = ov.topic_keywords
            .split(',')
            .map(k => k.trim().toLowerCase())
            .filter(Boolean);
          return keywords.some(k => lower.includes(k));
        });

        setMatched(matches);
      } catch {
        // Non-critical — fail silently
      }
    };

    loadAndMatch();
  }, [questionText]);

  const visible = matched.filter(m => !dismissed.has(m.id));
  if (!visible.length) return null;

  return (
    <div className="space-y-2 mb-3">
      {visible.map(ov => (
        <div
          key={ov.id}
          className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg animate-fade-in"
        >
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800 mb-0.5">ΠΡΟΣΟΧΗ — Ενημέρωση από διαχειριστή</p>
            <p className="text-xs text-amber-700 leading-relaxed">{ov.override_text}</p>
            {ov.source_url && (
              <a
                href={ov.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 mt-1"
              >
                <ExternalLink size={10} />
                Επίσημη πηγή
              </a>
            )}
          </div>
          <button
            onClick={() => setDismissed(prev => new Set([...prev, ov.id]))}
            className="text-amber-400 hover:text-amber-600 shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
